import { useState, useEffect } from 'react';
import { ChevronLeft, ChevronRight, Check, Dumbbell } from 'lucide-react';
import { Button } from '../ui';
import { OptionalSetForm } from './OptionalSetForm';

export function OptionalExercisePlayer({
  exercises,
  currentIndex,
  exerciseData,
  onExerciseDataUpdate,
  onNavigate,
  onComplete,
  onExit,
}) {
  const currentExercise = exercises[currentIndex];
  const existingData = exerciseData[currentExercise?.id] || { sets: [{}, {}, {}] };
  
  const [localSets, setLocalSets] = useState(existingData.sets || [{}, {}, {}]);
  const [activeSetIndex, setActiveSetIndex] = useState(0);

  // Reset local data when exercise changes
  useEffect(() => {
    const data = exerciseData[currentExercise?.id];
    setLocalSets(data?.sets || [{}, {}, {}]);
    setActiveSetIndex(0);
  }, [currentIndex, currentExercise?.id, exerciseData]);

  if (!currentExercise) {
    return (
      <div className="flex-1 flex items-center justify-center p-4">
        <p className="text-gray-500">No exercise found</p>
      </div>
    );
  }

  const handleSetUpdate = (setIndex, data) => {
    const newSets = [...localSets];
    newSets[setIndex] = data;
    setLocalSets(newSets);
    
    // Save to parent
    onExerciseDataUpdate(currentExercise.id, { sets: newSets });
  };

  const handleApplyToSet2 = () => {
    const sourceData = localSets[activeSetIndex];
    const newSets = [...localSets];
    newSets[1] = { ...sourceData };
    setLocalSets(newSets);
    onExerciseDataUpdate(currentExercise.id, { sets: newSets });
  };

  const handleApplyToSet3 = () => {
    const sourceData = localSets[activeSetIndex];
    const newSets = [...localSets];
    newSets[2] = { ...sourceData };
    setLocalSets(newSets);
    onExerciseDataUpdate(currentExercise.id, { sets: newSets });
  };

  const handleNext = () => {
    if (currentIndex < exercises.length - 1) {
      onNavigate(currentIndex + 1);
    }
  };

  const handlePrevious = () => {
    if (currentIndex > 0) {
      onNavigate(currentIndex - 1);
    }
  };

  const isLastExercise = currentIndex === exercises.length - 1;
  
  // Check if current exercise has any documented sets
  const hasDocumentedSets = localSets.some(set => 
    Object.keys(set).some(key => key !== 'notes' && set[key])
  );

  return (
    <div className="flex-1 flex flex-col overflow-hidden">
      {/* Progress Bar */}
      <div className="bg-white border-b border-gray-200 px-4 py-2">
        <div className="flex items-center justify-between text-sm text-gray-500 mb-1">
          <span>Exercise {currentIndex + 1} of {exercises.length}</span>
          <span>{Math.round(((currentIndex + 1) / exercises.length) * 100)}%</span>
        </div>
        <div className="w-full bg-gray-200 rounded-full h-1.5">
          <div
            className="h-1.5 rounded-full bg-primary transition-all"
            style={{ width: `${((currentIndex + 1) / exercises.length) * 100}%` }}
          />
        </div>

        {/* Exercise Pills */}
        <div className="flex gap-1.5 mt-3 overflow-x-auto pb-1">
          {exercises.map((ex, idx) => {
            const hasData = exerciseData[ex.id]?.sets?.some(set => 
              Object.keys(set).some(key => key !== 'notes' && set[key])
            );
            const isCurrent = idx === currentIndex;
            
            return (
              <button
                key={ex.id}
                onClick={() => onNavigate(idx)}
                className={`
                  flex-shrink-0 px-3 py-1 rounded-full text-xs font-medium transition-all
                  ${isCurrent 
                    ? 'bg-primary text-white' 
                    : hasData 
                      ? 'bg-green-100 text-green-700'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }
                `}
              >
                {hasData && !isCurrent && <Check className="h-3 w-3 inline mr-1" />}
                {idx + 1}
              </button>
            );
          })}
        </div>
      </div>

      {/* Scrollable Content */}
      <div className="flex-1 overflow-y-auto">
        <div className="p-4 space-y-4">
          {/* Exercise Header */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
            <div className="flex items-start">
              <div className="h-12 w-12 rounded-full bg-primary/10 flex items-center justify-center mr-4 flex-shrink-0">
                <Dumbbell className="h-6 w-6 text-primary" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-gray-900">
                  {currentExercise.exercise}
                </h2>
                <p className="text-primary font-medium mt-1">
                  {currentExercise.documentation}
                </p>
              </div>
            </div>
          </div>

          {/* Set Tabs */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
            <div className="flex border-b border-gray-200">
              {[0, 1, 2].map((setIndex) => {
                const setData = localSets[setIndex] || {};
                const hasSetData = Object.keys(setData).some(key => key !== 'notes' && setData[key]);
                
                return (
                  <button
                    key={setIndex}
                    onClick={() => setActiveSetIndex(setIndex)}
                    className={`
                      flex-1 py-3 text-sm font-medium transition-colors relative
                      ${activeSetIndex === setIndex 
                        ? 'text-primary border-b-2 border-primary bg-primary/5' 
                        : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
                      }
                    `}
                  >
                    Set {setIndex + 1}
                    {hasSetData && (
                      <span className="ml-1.5 inline-flex items-center justify-center h-4 w-4 rounded-full bg-green-500 text-white text-xs">
                        ✓
                      </span>
                    )}
                  </button>
                );
              })}
            </div>

            {/* Set Form */}
            <div className="p-4">
              <OptionalSetForm
                exercise={currentExercise}
                setIndex={activeSetIndex}
                data={localSets[activeSetIndex] || {}}
                onUpdate={(data) => handleSetUpdate(activeSetIndex, data)}
                onApplyToSet2={handleApplyToSet2}
                onApplyToSet3={handleApplyToSet3}
                showApplyToSet2={activeSetIndex === 0}
                showApplyToSet3={activeSetIndex === 0 || activeSetIndex === 1}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Navigation */}
      <div className="bg-white border-t border-gray-200 p-4 flex gap-3">
        <Button
          variant="outline"
          onClick={handlePrevious}
          disabled={currentIndex === 0}
          className="flex-shrink-0"
        >
          <ChevronLeft className="h-5 w-5" />
        </Button>

        {isLastExercise ? (
          <Button
            onClick={onComplete}
            fullWidth
          >
            <Check className="h-5 w-5 mr-2" />
            Complete Session
          </Button>
        ) : (
          <Button
            onClick={handleNext}
            fullWidth
          >
            Next Exercise
            <ChevronRight className="h-5 w-5 ml-2" />
          </Button>
        )}
      </div>
    </div>
  );
}